import React from 'react'
import Helmet from 'react-helmet'
import { isEmpty } from 'lodash'
import { PropTypes, object, string, func, bool } from 'prop-types'
import { connect } from 'react-redux'
import { StaticQuery, navigate, graphql } from 'gatsby'
import { decodeTrademarkEntity, getLastParamOfPath } from '@helpers/string-helper'
import { productUrl } from '@helpers/route'
import { getRoomPackageUpgrades } from '@helpers/room-configurations'
import { checkPLALocation, generateSeeInRoomObjects } from '@helpers/static-pdp'
import {
  availabilityStockMessage,
  enhanceStockMessage,
  productAvailability,
  sortByPriceDescending,
} from '@helpers/product'
import { fetchProductBySku, fetchProductWarehouseAvailability } from '@services/product'
import { getCurrentLocation } from '@helpers/geo-location'
import { setupAnalytics, analyticsProduct } from '@helpers/google-tag-manager'
import { getCanonicalUrl } from '@helpers/canonical'
import { fetchPromotions, getAlgoliaRecommendation } from '@services/checkout'
import Cookies from 'universal-cookie'
import { basename } from 'path-browserify'
import 'url-search-params-polyfill'
import { v4 as uuidv4 } from 'uuid'
import { setLocation, setShippingAddress } from '@redux/modules/location'
import styled from 'styled-components'
import { colors } from '@constants/styles'
import Layout from '../components/layout'
import ProductDetail from '../components/product/ProductDetail'
import ProductLIA from '../components/product/product-lia/product-lia-wrapper'
import '../assets/css/pages/product.sass'

// Some styles are not computed correctly in the page served from server. Manually correcting them here.
const ProductDetailWrapper = styled.div`
  font-weight: 400;
  background-color: ${colors.lighterGrey};
`

const needsLocation = (rtg_location, product) =>
  rtg_location &&
  product &&
  (rtg_location.region !== 'OOM' ||
    ((product.delivery_type === 'O' || product.delivery_type === 'U') && rtg_location.region === 'OOM'))

class ProductTemplate extends React.Component {
  constructor(props) {
    super()
    const { serverData } = props
    this.state = {
      storeNum: null,
      roomPackageUpgrades: serverData?.room_package_upgrades || [],
    }
  }

  componentDidMount() {
    const { dispatch } = this.props
    checkPLALocation(dispatch)
    this.setUp()
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'optimize.activate' })
    }
  }

  componentDidUpdate(prevProps) {
    // eslint-disable-next-line react/destructuring-assignment
    if (prevProps['*'] !== this.props['*']) {
      this.setUp()
    }
  }

  setUp = async () => {
    const {
      location: { pathname, search },
    } = this.props
    const sku = getLastParamOfPath()
    if (sku && new RegExp('[0-9]{8}|[0-9]{7}P|[0-9]{7}p').test(sku)) {
      this.setUpProductData()
      const query = new URLSearchParams(search)
      this.setState({
        storeNum: query && query.get('storeCode'),
      })
    } else {
      // TODO: navigation need to add code to track ga data
      navigate('/404', { replace: true })
    }
  }

  pageAnalytics(product) {
    if (window && product) {
      setupAnalytics({
        pageData: {
          type: 'product',
          title: product.title,
          path: productUrl(product.route),
        },
      })
      window.dataLayer.push({
        event: 'ee_detail',
        ecommerce: { detail: { products: [analyticsProduct(product)] } },
      })
    }
  }

  setUpProductData = async () => {
    const rtg_location = getCurrentLocation()
    const {
      location: { pathname },
      serverData,
    } = this.props
    const { availabilityInfo, productData: product, see_in_room } = serverData
    let stockMessage
    if (window) {
      const upgrades = await getRoomPackageUpgrades(product)
      this.setState({ roomPackageUpgrades: upgrades })
    }
    if (needsLocation(rtg_location, product)) {
      try {
        const { data } = availabilityInfo
        /* if the product type is a room and the items are eligible for split delivery, show a unique message */
        if (
          Object.is(product?.type, 'room') &&
          data?.availabilities[0]?.splitDeliveryMessage &&
          data?.availabilities[0].catalog_availability[rtg_location.region]
        ) {
          stockMessage = enhanceStockMessage(data.availabilities[0].splitDeliveryMessage)
        } else {
          /* otherwise, show the standard availability message */
          stockMessage = availabilityStockMessage({ availableOn: data.earliestAvailability, product, rtg_location })
        }
        this.pageAnalytics({ ...product, stockMessage, availabilityInfo })
      } catch (err) {
        stockMessage = 'Out of Stock '
      }
    }

    if (!pathname.includes(product.route)) {
      // TODO: navigation need to add code to track ga data
      navigate('/404', { replace: true })
    }
  }

  render() {
    const {
      pageContext,
      rtg_location,
      order,
      shipping_address,
      cart,
      isLandscape,
      onSetCart,
      onSetLocation,
      onSetOrder,
      onSetShippingAddress,
      serverData,
      dispatch,
    } = this.props
    const { middleBanner } = pageContext || null
    const { productData: product, availabilityInfo, see_in_room, location } = serverData
    const { storeNum, roomPackageUpgrades } = this.state
    const room = !product.see_in_room
    const { region } = location
    const isAvailableInCurrentRegion =
      availabilityInfo?.data?.availabilities?.[0]?.catalog_availability?.[region] || false
    const availabilityDate = isAvailableInCurrentRegion
      ? availabilityInfo?.data?.availabilities?.[0]?.availableOn || ''
      : ''
    product.availabilityInfo = availabilityInfo

    return (
      <ProductDetailWrapper>
        <Layout
          rtg_location={rtg_location}
          order={order}
          shipping_address={shipping_address}
          cart={cart}
          isLandscape={isLandscape}
          onSetCart={onSetCart}
          onSetLocation={onSetLocation}
          onSetOrder={onSetOrder}
          onSetShippingAddress={onSetShippingAddress}
          isProductPage
        >
          <Helmet
            title={`${product?.title ? decodeTrademarkEntity(product.title) : 'Product'} - Rooms To Go`}
            link={[
              {
                rel: 'canonical',
                href: getCanonicalUrl(productUrl(product.route)),
              },
            ]}
          />
          {/* Allow ProductDetail to render with either initialProductData or fullProductData */}
          {product && typeof product !== 'undefined' && !storeNum && product.title && (
            <ProductDetail
              product={product}
              see_in_room={see_in_room}
              room={room}
              bannerPlacementMiddle={middleBanner}
              financeBanners={pageContext && pageContext.financingBanners}
              bannerBySku={pageContext?.bannerBySku}
              room_package_upgrades={roomPackageUpgrades}
              availabilityDate={availabilityDate}
            />
          )}
          {/* Product LIA only renders with fullProductData */}
          {product && typeof product !== 'undefined' && storeNum && product.title && (
            <StaticQuery
              query={graphql`
                query {
                  allStrapiStore {
                    edges {
                      node {
                        ...StrapiStoreFragment
                      }
                    }
                  }
                }
              `}
              render={data => {
                const store = data.allStrapiStore.edges.find(loc => loc.node.StoreNumber === Number(storeNum))
                if (store) {
                  return <ProductLIA product={product} store={store.node} />
                }
                return (
                  <ProductDetail
                    product={product}
                    see_in_room={see_in_room}
                    room={room}
                    bannerPlacementMiddle={middleBanner}
                    financeBanners={pageContext && pageContext.financingBanners}
                    bannerBySku={pageContext?.bannerBySku}
                    room_package_upgrades={roomPackageUpgrades}
                    availabilityDate={availabilityDate}
                  />
                )
              }}
            />
          )}
        </Layout>
      </ProductDetailWrapper>
    )
  }
}

ProductTemplate.propTypes = {
  pageContext: object,
  serverData: object,
  location: object,
  dispatch: func,
  '*': string,
  isLandscape: bool,
  rtg_location: object,
  order: object,
  shipping_address: string,
  cart: PropTypes.shape({
    cartItems: PropTypes.array,
  }),
  onSetCart: func,
  onSetLocation: func,
  onSetOrder: func,
  onSetShippingAddress: func,
}

export default connect()(ProductTemplate)

/*  Fetch data for serverside page */

const getSeeInRoomProduct = async (product, region) => {
  const regionalSeeInRoom = product?.see_in_room?.[region]
  if (regionalSeeInRoom) {
    const roomProduct = await fetchProductBySku(regionalSeeInRoom[0].sku)
    const seeInRoom = generateSeeInRoomObjects(roomProduct)
    return seeInRoom
  }
  return undefined
}

const fetchPromotionInfo = async (product, location) => {
  const { region, zone } = location
  const promotion = product?.promotions?.[`${region}_${zone}`] || product?.promotions?.[`${region}_0`]
  const target = promotion?.target_skus?.[0]
  const targetQuantity = promotion?.target_value
  const qualifierQuantity = promotion?.qualifier_value
  const promoType = promotion?.offer_template
  let promotionData = null
  if (target && targetQuantity && qualifierQuantity) {
    const requestItems = [
      {
        sku: product.sku,
        quantity: parseInt(qualifierQuantity),
      },
      {
        sku: target.sku,
        quantity: promoType === 'BUY X AND Y GET Z DISCOUNT' ? 2 : parseInt(targetQuantity),
      },
    ]
    promotionData = await fetchPromotions({ requestItems, region, zone })
  }
  return promotionData
}

const fetchSimilarItems = async (product, location) => {
  const { sku } = product
  const { region } = location

  const data = await getAlgoliaRecommendation({ sku, region })
  const availabileSimilarItems = data.filter(item => productAvailability(item, region)) ?? null
  const similarItems = sortByPriceDescending(availabileSimilarItems)
  return similarItems
}

const fetchPDPData = async (headers, requestId, productData) => {
  const pdpData = {}
  try {
    const serverCookie = new Cookies(headers.get('cookie'))
    const location = getCurrentLocation(serverCookie)
    pdpData.location = location
    // eslint-disable-next-line no-console
    console.log(JSON.stringify({ location, requestId }))
    // Populate as much data as we can

    await Promise.allSettled([
      fetchProductWarehouseAvailability(productData.sku, location.distribution_index, location.state).then(
        availabilityInfo => {
          pdpData.availabilityInfo = availabilityInfo
        },
      ),
      getSeeInRoomProduct(productData, location.region).then(see_in_room => {
        pdpData.see_in_room = see_in_room
      }),
      fetchPromotionInfo(productData, location).then(promotionData => {
        pdpData.promotionData = promotionData
      }),
      fetchSimilarItems(productData, location).then(similarItems => {
        productData.similarItems = similarItems
      }),
    ])
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log({ requestId, message: 'Failed to retrieve pdp data.', error: error.message, url: error?.config?.url })
  }
  return pdpData
}

export async function getServerData(context) {
  const requestId = uuidv4()
  try {
    const { headers, method, url, query, params } = context
    // eslint-disable-next-line no-console
    console.log(JSON.stringify({ headers: Object.fromEntries(headers), method, url, query, params, requestId }))
    const sku = basename(context.params['*'])
    const productData = await fetchProductBySku(sku)
    if (isEmpty(productData)) {
      throw new Error(`Failed to retrieve product data`)
    }
    const { location, see_in_room, availabilityInfo, promotionData } = await fetchPDPData(
      headers,
      requestId,
      productData,
    )
    productData.promotionData = promotionData
    const props = {
      productData,
      availabilityInfo,
      see_in_room,
      pageContext: context.pageContext,
      location,
    }
    // eslint-disable-next-line no-console
    console.log(JSON.stringify({ includedProps: Object.keys(props), availabilityInfo, location, requestId }))
    return {
      props,
      headers: {
        'x-ssr-page': true,
      },
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(JSON.stringify({ level: 'Error', message: err.message, url: context.url, requestId }))
    return { status: 500 }
  }
}
